import * as React from 'react'

// Przeglądarka nie ma dostępu do TWAIN/WIA, więc rolę WPF-owego WpfScannerProvider
// pełni mostek: mały proces na Windowsie użytkownika, referujący ten sam
// AligatorEngine.dll. Wzór dokładnie jak mostek czytnika kart (useWebcardReader).
export const SCAN_BRIDGE_URL = 'http://localhost:7010'
export const SCAN_BRIDGE_DOWNLOAD_URL = '/assets/EasyTachoScanner-setup.exe'

// Mostek nie aktualizuje się sam - instaluje go użytkownik. To JEDYNE miejsce do
// zmiany, gdy nowa aplikacja wymaga nowszego mostka: data budowy jest stemplowana
// automatycznie (tools/scan-bridge/src/Directory.Build.props), więc po stronie
// mostka nie ma czego pamiętać i nie ma się czemu rozjechać.
export const SCAN_BRIDGE_MIN_BUILD = '2026-09-23'

// Odpowiednik AligatorEngine ScannerProperties.
export type BridgeScanner = {
  deviceId: string
  name: string
  driverId: string
}

export type ScannerBridgeStatus =
  | 'checking'
  | 'unavailable'
  | 'no-scanner'
  | 'ready'
  | 'error'

export type ScanProgress = {
  pageCount: number
  transferring: boolean
}

export type ScanBridgeLog = {
  supported: boolean
  lines: string[]
}

export type ScanOptions = {
  brightness?: number
  showScannerDialog?: boolean
}

const PING_TIMEOUT_MS = 2000
const POLL_INTERVAL_MS = 500
// Licznik chodzi od ostatniego postępu, nie od startu: skan całego podajnika
// trwa długo, ale przerwa bez nowej strony oznacza, że coś stanęło.
const STALL_TIMEOUT_MS = 180_000

// Przeglądarka nie widzi dysku ani rejestru, więc "zainstalowane, ale wyłączone"
// jest dla niej nieodróżnialne od "nigdy nie zainstalowane". Zapamiętujemy więc
// fakt, że mostek kiedyś tu odpowiadał - to wystarcza, żeby zamiast wysyłać
// użytkownika po instalator, kazać mu po prostu uruchomić program.
const SEEN_STORAGE_KEY = 'et.scanBridge.lastSeen'

// Domyślny skaner jest per przeglądarka, nie per firma. WPF trzyma go w bazie, bo
// działa na jednym stanowisku; tę samą firmę otwiera się z wielu komputerów, więc
// deviceId zapisany centralnie wskazywałby cudze urządzenie.
const PREFERRED_STORAGE_KEY = 'et.scanBridge.preferredDevice'

export const getPreferredScanner = (): string | null => {
  try {
    return window.localStorage.getItem(PREFERRED_STORAGE_KEY)
  } catch {
    return null
  }
}

export const setPreferredScanner = (deviceId: string | null) => {
  try {
    if (deviceId) window.localStorage.setItem(PREFERRED_STORAGE_KEY, deviceId)
    else window.localStorage.removeItem(PREFERRED_STORAGE_KEY)
  } catch {
    // tryb prywatny - wybór po prostu nie przetrwa sesji
  }
}

const rememberSeen = () => {
  try {
    window.localStorage.setItem(SEEN_STORAGE_KEY, new Date().toISOString())
  } catch {
    // tryb prywatny albo zablokowane dane witryny - trudno, wracamy do podpowiedzi instalacji
  }
}

const wasSeenBefore = () => {
  try {
    return Boolean(window.localStorage.getItem(SEEN_STORAGE_KEY))
  } catch {
    return false
  }
}

const errorFrom = (payload: unknown, fallback: string) => {
  const message = (payload as { error?: string } | null)?.error
  return message && message.length ? message : fallback
}

export const useScannerBridge = () => {
  const [status, setStatus] = React.useState<ScannerBridgeStatus>('checking')
  const [scanners, setScanners] = React.useState<BridgeScanner[]>([])
  const [error, setError] = React.useState<string | null>(null)
  const [version, setVersion] = React.useState<string | null>(null)
  const [buildDate, setBuildDate] = React.useState<string | null>(null)
  const [scanning, setScanning] = React.useState(false)
  const [progress, setProgress] = React.useState<ScanProgress | null>(null)
  const [retryKey, setRetryKey] = React.useState(0)
  const [seenBefore] = React.useState(wasSeenBefore)

  // Sesja żyje w mostku niezależnie od komponentu. Bez tego zamknięcie modala
  // zostawiało skan w toku, a każdy kolejny dostawał 409.
  const session = React.useRef<{ id: string | null; cancelled: boolean }>({
    id: null,
    cancelled: false,
  })

  // abort=true przerywa skan w toku; bez niego tylko zwalniamy strony z pamięci
  // mostka po udanym pobraniu.
  const releaseSession = React.useCallback((id: string, abort: boolean) => {
    if (abort) {
      fetch(`${SCAN_BRIDGE_URL}/scan/${id}/cancel`, { method: 'POST', keepalive: true }).catch(() => {})
    }
    fetch(`${SCAN_BRIDGE_URL}/scan/${id}`, { method: 'DELETE', keepalive: true }).catch(() => {})
  }, [])

  const cancel = React.useCallback(() => {
    session.current.cancelled = true
    if (session.current.id) releaseSession(session.current.id, true)
  }, [releaseSession])

  React.useEffect(() => () => cancel(), [cancel])

  const retry = React.useCallback(() => {
    setStatus('checking')
    setError(null)
    setRetryKey((k) => k + 1)
  }, [])

  React.useEffect(() => {
    const cancelled = { current: false }

    const probe = async () => {
      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), PING_TIMEOUT_MS)
      try {
        const ping = await fetch(`${SCAN_BRIDGE_URL}/ping`, { signal: controller.signal })
        if (!ping.ok) throw new Error('ping failed')
        rememberSeen()
        const info = await ping.json().catch(() => null)
        if (!cancelled.current) {
          setVersion(info?.version ?? null)
          setBuildDate(info?.buildDate ?? null)
        }
      } catch {
        if (!cancelled.current) setStatus('unavailable')
        return
      } finally {
        clearTimeout(timeout)
      }

      if (cancelled.current) return

      try {
        const res = await fetch(`${SCAN_BRIDGE_URL}/scanners`)
        const body = await res.json()
        // Bez tego błąd sterownika (500) wyglądał jak "brak skanera" i wysyłał
        // użytkownika sprawdzać kabel zamiast pokazać, co naprawdę się stało.
        if (!res.ok) throw new Error(errorFrom(body, `HTTP ${res.status}`))

        const list: BridgeScanner[] = body?.scanners ?? []
        if (cancelled.current) return
        setScanners(list)
        setStatus(list.length ? 'ready' : 'no-scanner')
      } catch (e: unknown) {
        if (cancelled.current) return
        setError(e instanceof Error ? e.message : String(e))
        setStatus('error')
      }
    }

    probe()
    return () => {
      cancelled.current = true
    }
  }, [retryKey])

  // Skan z podajnika trwa i oddaje wiele stron, więc mostek nie blokuje odpowiedzi:
  // POST /scan zakłada sesję, a my dopytujemy o postęp (jak reszta importów w tym
  // module). Strony schodzą binarnie - base64 puchłoby o 1/3.
  const scan = React.useCallback(
    async (scanner: BridgeScanner, options: ScanOptions = {}): Promise<File[]> => {
      setScanning(true)
      setError(null)
      setProgress({ pageCount: 0, transferring: false })
      session.current = { id: null, cancelled: false }

      const abandoned = () => session.current.cancelled

      try {
        const started = await fetch(`${SCAN_BRIDGE_URL}/scan`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            deviceId: scanner.deviceId,
            driverId: scanner.driverId,
            brightness: options.brightness ?? 50,
            showScannerDialog: options.showScannerDialog ?? false,
          }),
        })
        const startedBody = await started.json()
        if (!started.ok) throw new Error(errorFrom(startedBody, 'scan failed'))
        session.current.id = startedBody.sessionId as string

        let pageCount = 0
        let lastProgressAt = Date.now()

        for (;;) {
          await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS))
          if (abandoned()) return []

          const res = await fetch(`${SCAN_BRIDGE_URL}/scan/${session.current.id}`)
          const state = await res.json()
          if (!res.ok) throw new Error(errorFrom(state, 'scan failed'))
          if (state.state === 'error') throw new Error(errorFrom(state, 'scan failed'))

          if ((state.pageCount ?? 0) !== pageCount) {
            pageCount = state.pageCount ?? 0
            lastProgressAt = Date.now()
            setProgress({ pageCount, transferring: false })
          }

          if (state.state === 'done') break

          if (Date.now() - lastProgressAt > STALL_TIMEOUT_MS) {
            throw new Error('Skaner nie odpowiada.')
          }
        }

        // Windows kończy na zero stronach, gdy użytkownik zamknie okno sterownika;
        // bez tego spinner po prostu znikał i klik wyglądał na nieskuteczny.
        if (pageCount === 0) {
          throw new Error('Skaner nie zwrócił żadnej strony.')
        }

        setProgress({ pageCount, transferring: true })
        const stamp = new Date().toISOString().replace(/[-:T]/g, '').slice(0, 14)
        const files: File[] = []

        for (let i = 0; i < pageCount; i++) {
          if (abandoned()) return []
          const res = await fetch(`${SCAN_BRIDGE_URL}/scan/${session.current.id}/page/${i}`)
          if (!res.ok) throw new Error('page download failed')
          const blob = await res.blob()

          // Windows oddaje 1bpp BMP, SANE bezstratny PNG. Nazwa i typ muszą iść za
          // odpowiedzią - inaczej PNG jedzie jako .bmp i serwer czyta jego nagłówek
          // jak BMP, wpisując bzdurne wymiary skanu.
          const type = res.headers.get('Content-Type') || blob.type || 'application/octet-stream'
          const ext = type.includes('png') ? 'png' : type.includes('jpeg') ? 'jpg' : 'bmp'
          files.push(new File([blob], `scan_${stamp}_${i + 1}.${ext}`, { type }))
        }

        return files
      } catch (e: unknown) {
        if (abandoned()) return []
        setError(e instanceof Error ? e.message : String(e))
        throw e
      } finally {
        const id = session.current.id
        if (id) releaseSession(id, session.current.cancelled)
        session.current = { id: null, cancelled: false }
        setScanning(false)
        setProgress(null)
      }
    },
    [releaseSession],
  )

  // Log mostka na żądanie - zaciągany dopiero, gdy użytkownik rozwinie szczegóły.
  // 1.0.0 nie ma jeszcze /log, więc brak wsparcia trzeba odróżnić od pustego logu:
  // inaczej klient ze starą wersją widzi "log jest pusty" i nie ma czego wkleić.
  const fetchLog = React.useCallback(async (): Promise<ScanBridgeLog> => {
    try {
      const res = await fetch(`${SCAN_BRIDGE_URL}/log?lines=200`)
      if (res.status === 404) return { supported: false, lines: [] }
      if (!res.ok) return { supported: true, lines: [] }
      return { supported: true, lines: (await res.json())?.lines ?? [] }
    } catch {
      return { supported: false, lines: [] }
    }
  }, [])

  // Daty ISO porównują się leksykograficznie. Brak buildDate = mostek sprzed
  // wprowadzenia stempla, czyli na pewno starszy niż wymagany.
  const outdated =
    status !== 'checking' && status !== 'unavailable' &&
    (buildDate == null || buildDate < SCAN_BRIDGE_MIN_BUILD)

  return {
    status, scanners, error, scanning, progress, scan, cancel, retry,
    version, buildDate, outdated, fetchLog, seenBefore,
  }
}
